// Shared helpers for the LGRA Race Calendar + Find My Race experience.
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

export const fmtDate = (iso) => {
  try {
    const d = new Date(iso + 'T00:00:00');
    return `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)} ${d.getFullYear()}`;
  } catch { return iso; }
};

export const monthKey = (iso) => (iso || '').slice(0, 7);

export const monthLabel = (key) => {
  const [y, m] = (key || '').split('-');
  return `${MONTHS[parseInt(m, 10) - 1]} ${y}`;
};

// Map raw distance tags ("5km", "10km", "21km", "42km", "3.5km", "ultra") to
// the coarse buckets used by the distance filter. Anything that doesn't fall
// into one of the four common distances is left out so it lands under "Others".
export const distanceBuckets = (distances = []) => {
  const s = new Set();
  for (const d of distances || []) {
    const ds = String(d).toLowerCase().replace(/\s/g, '');
    if (ds.includes('ultra')) { s.add('other'); continue; }
    if (ds.includes('half')) { s.add('21km'); continue; }
    const m = ds.match(/(\d+(\.\d+)?)/);
    const num = m ? parseFloat(m[1]) : null;
    if (num != null) {
      if (num <= 6) s.add('short');
      else if (num >= 9 && num <= 12) s.add('10km');
      else if (num >= 20 && num <= 21.2) s.add('21km');
      else if (num >= 40 && num <= 43) s.add('42km');
      else s.add('other');
    } else if (ds.includes('marathon')) {
      s.add('42km');
    }
  }
  return s;
};

// Buckets that count as a recognised common distance (used to decide "Others").
export const COMMON_DISTANCE_BUCKETS = ['short', '10km', '21km', '42km'];

export const DISTANCE_FILTERS = [
  { key: 'all', label: 'All distances' },
  { key: 'short', label: '5 km / Short' },
  { key: '10km', label: '10 km' },
  { key: '21km', label: 'Half Marathon (21 km)' },
  { key: '42km', label: 'Marathon (42 km)' },
  { key: 'others', label: 'Others' },
];

// ── Weekend grouping for the full Malaysia list ──────────────────────────
// A soft colour per weekend number, used consistently across the filter chips,
// the group heading/banner, the group container tint AND each event row's left
// accent bar — so every part of a weekend shares the same colour cue.
//   chip  → filter chip + heading banner (soft bg + readable text + border)
//   dot   → the little status dot
//   bar   → hex used for each event row's left accent border (inline style)
//   wrap  → soft group container (very light tint + matching border)
export const WEEKEND_COLORS = {
  1: { chip: 'bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]', dot: 'bg-[#3B82F6]', bar: '#3B82F6', wrap: 'bg-[#F5F9FF] border-[#DBEAFE]' },
  2: { chip: 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]', dot: 'bg-[#10B981]', bar: '#10B981', wrap: 'bg-[#F3FDF8] border-[#D1FAE5]' },
  3: { chip: 'bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]', dot: 'bg-[#F97316]', bar: '#F97316', wrap: 'bg-[#FFFAF4] border-[#FFEDD5]' },
  4: { chip: 'bg-[#FDF4FF] text-[#A21CAF] border-[#F5D0FE]', dot: 'bg-[#D946EF]', bar: '#D946EF', wrap: 'bg-[#FDF7FF] border-[#FAE8FF]' },
  5: { chip: 'bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]', dot: 'bg-[#EF4444]', bar: '#EF4444', wrap: 'bg-[#FFF6F6] border-[#FEE2E2]' },
};
export const weekendColor = (n) => WEEKEND_COLORS[n] || WEEKEND_COLORS[5];

// All Sat–Sun weekends that fall inside a given 'YYYY-MM'.
export const monthWeekends = (mKey) => {
  if (!mKey) return [];
  const [y, m] = mKey.split('-').map(Number);
  if (!y || !m) return [];
  const first = new Date(y, m - 1, 1);
  const last = new Date(y, m, 0);
  const out = [];
  const day = new Date(first);
  day.setDate(1 + ((6 - first.getDay() + 7) % 7)); // first Saturday of the month
  let n = 1;
  while (day <= last) {
    const sat = new Date(day);
    const sun = new Date(day);
    sun.setDate(day.getDate() + 1);
    out.push({ num: n, sat, sun });
    day.setDate(day.getDate() + 7);
    n += 1;
  }
  return out;
};

// Which weekend (1..5) a race date belongs to within its month. Weekday races
// map to the weekend of their week (nearest Saturday).
export const weekendNumForDate = (iso, mKey) => {
  const weekends = monthWeekends(mKey || monthKey(iso));
  if (!weekends.length) return 1;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return 1;
  const dow = d.getDay();
  const sat = new Date(d);
  if (dow === 0) sat.setDate(d.getDate() - 1);
  else sat.setDate(d.getDate() + (6 - dow));
  let best = weekends[0].num;
  let diff = Infinity;
  for (const w of weekends) {
    const dd = Math.abs(w.sat.getTime() - sat.getTime());
    if (dd < diff) { diff = dd; best = w.num; }
  }
  return best;
};

// "3–4 October" (or "31 Oct – 1 Nov" across a month boundary).
export const weekendDatesLabel = (w) => {
  if (!w) return '';
  const d = (dt) => dt.getDate();
  const mn = (dt) => dt.toLocaleString('en-US', { month: 'long' });
  if (w.sat.getMonth() === w.sun.getMonth()) return `${d(w.sat)}–${d(w.sun)} ${mn(w.sat)}`;
  return `${d(w.sat)} ${mn(w.sat)} – ${d(w.sun)} ${mn(w.sun)}`;
};
